import { useCallback } from 'react';
import { TASK_FILTER_PROPERTY_TYPE, TASK_LIST_TYPE } from '../../global/constants';
import { isEmpty } from '../../helpers/common';
import { updateWorkspaceProjectPreference } from '../../helpers/firebaseHelper';
import { CurrentProject } from '../../interfaces/ProjectInterface';

interface UseAccountingDisplayPropertiesProps {
  currentProject: CurrentProject;
  taskFilter: NonNullable<CurrentProject>['taskFilterType'] | undefined;
}

export const useAccountingDisplayProperties = ({
  currentProject,
  taskFilter
}: UseAccountingDisplayPropertiesProps) => {
  const getDisplayProperties = useCallback(() => {
    const viewType = taskFilter?.taskViewType;
    const properties: string[] = [];
    if (viewType === TASK_LIST_TYPE.TASKS || viewType === TASK_LIST_TYPE.TASK_GRID) {
      if (!isEmpty(currentProject?.identifier)) properties.push(TASK_FILTER_PROPERTY_TYPE.ID);
      properties.push(TASK_FILTER_PROPERTY_TYPE.STATUS);
      if (currentProject?.priority?.enabled) properties.push(TASK_FILTER_PROPERTY_TYPE.PRIORITY);
      properties.push(TASK_FILTER_PROPERTY_TYPE.ASSIGNEE);
      if (currentProject?.labelsEnabled) properties.push(TASK_FILTER_PROPERTY_TYPE.LABELS);
      if (viewType === TASK_LIST_TYPE.TASK_GRID && currentProject?.isMilestone) {
        properties.push(TASK_FILTER_PROPERTY_TYPE.MILESTONE);
      }
      properties.push(TASK_FILTER_PROPERTY_TYPE.DUE_DATE);
    } else if (viewType === TASK_LIST_TYPE.CALENDAR_VIEW) {
      if (!isEmpty(currentProject?.identifier)) properties.push(TASK_FILTER_PROPERTY_TYPE.ID);
      if (currentProject?.priority?.enabled) properties.push(TASK_FILTER_PROPERTY_TYPE.PRIORITY);
      properties.push(TASK_FILTER_PROPERTY_TYPE.ASSIGNEE);
      if (currentProject?.labelsEnabled) properties.push(TASK_FILTER_PROPERTY_TYPE.LABELS);
    }
    return properties;
  }, [currentProject, taskFilter?.taskViewType]);

  const handleDisplayPropertyToggle = useCallback(
    async (propertyType: string) => {
      if (!currentProject) return;
      const filter = currentProject.displayPropertiesFilterWithFirebase as Record<string, boolean> | undefined;
      const newVisibility = !(filter?.[propertyType] !== false);
      currentProject.updateListItemsDisplayFilter(propertyType, newVisibility);
      // Write only the toggled property. updateWorkspaceProjectPreference uses
      // setDoc({ merge: true }), which deep-merges nested maps, so the prior read of the full
      // list_items_display_filter map (just to spread it back) was a redundant waterfall read.
      await updateWorkspaceProjectPreference(currentProject.id, {
        list_items_display_filter: { [propertyType]: newVisibility }
      });
    },
    [currentProject]
  );

  return { getDisplayProperties, handleDisplayPropertyToggle };
};
